import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { streamersData, getStreamerDisplayName } from "../App";
import CornerEmote from "./CornerEmote";
import "../StreamerPage.css";

function StreamerPage() {
  const { streamer, page } = useParams();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [limit] = useState(90);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalVideos: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });

  useEffect(() => {
    if (streamersData[streamer]) {
      const allVideos = streamersData[streamer];
      const query = searchQuery.trim().toLowerCase();
      const filteredVideos = query
        ? allVideos.filter((v) =>
            v.twitch_highlight_title.toLowerCase().includes(query)
          )
        : allVideos;

      if (query) {
        setVideos(filteredVideos);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalVideos: filteredVideos.length,
          hasNextPage: false,
          hasPrevPage: false,
        });
      } else {
        const currentPage = page ? parseInt(page) : 1;
        const startIndex = (currentPage - 1) * limit;
        const endIndex = currentPage * limit;
        const totalVideos = filteredVideos.length;
        const totalPages = Math.ceil(totalVideos / limit);
        const paginatedVideos = filteredVideos.slice(startIndex, endIndex);

        setVideos(paginatedVideos);
        setPagination({
          currentPage,
          totalPages,
          totalVideos,
          hasNextPage: currentPage < totalPages,
          hasPrevPage: currentPage > 1,
        });
      }
      setLoading(false);
    } else {
      setError("Streamer not found");
      setLoading(false);
    }
  }, [streamer, page, searchQuery, limit]);

  // Update page title based on streamer
  useEffect(() => {
    if (streamer) {
      document.title = `${getStreamerDisplayName(streamer)}`;
    }
  }, [streamer]);

  const handlePageChange = (newPage) => {
    setLoading(true);
    navigate(`/${streamer}/page/${newPage}`);
  };

  const handleSelectChange = (event) => {
    const page = parseInt(event.target.value);
    handlePageChange(page);
  };

  if (loading) {
    return <div className="streamer-page">Loading videos...</div>;
  }

  if (error) {
    return <div className="streamer-page">Error: {error}</div>;
  }

  return (
    <div className="streamer-page">
      <CornerEmote />
      <div className="streamer-header">
        <Link to="/" className="back-button">
          Назад
        </Link>
        <h1>{getStreamerDisplayName(streamer)}</h1>
      </div>

      <div className="toolbar">
        <div className="toolbar-spacer" aria-hidden="true" />
        <div className="toolbar-center">
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-button"
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrevPage}
              ></button>
              <div className="pagination-controls">
                <span className="pagination-info">
                  {pagination.currentPage} из {pagination.totalPages}
                </span>
                <select
                  className="pagination-select"
                  value={pagination.currentPage}
                  onChange={handleSelectChange}
                  aria-label="Select page"
                >
                  {Array.from(
                    { length: pagination.totalPages },
                    (_, i) => i + 1
                  ).map((pageNum) => (
                    <option key={pageNum} value={pageNum}>
                      {pageNum}
                    </option>
                  ))}
                </select>
              </div>
              <button
                className="pagination-button"
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNextPage}
              ></button>
            </div>
          )}
        </div>
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Поиск по названию..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="videos-grid">
        {videos.map((video) => (
          <div key={video.twitch_highlight_id} className="video-card">
            <Link
              to={
                video.external_source ||
                `/${streamer}/${video.twitch_highlight_id}`
              }
              state={
                video.external_source
                  ? null
                  : { fromPage: pagination.currentPage }
              }
              className="video-link"
              {...(video.external_source
                ? { target: "_blank", rel: "noopener noreferrer" }
                : {})}
            >
              <div
                className="video-thumbnail"
                style={{ backgroundColor: "#1f1f23" }}
              >
                <img
                  src={video.thumbnail_url}
                  alt={video.twitch_highlight_title}
                  className="thumbnail-image"
                />
              </div>
              <div className="video-info">
                <h3 className="video-title">{video.twitch_highlight_title}</h3>
                <p className="video-date">
                  {new Date(
                    video.twitch_highlight_created_at
                  ).toLocaleDateString("ru-RU")}
                </p>
              </div>
            </Link>
          </div>
        ))}
      </div>

      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-button"
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={!pagination.hasPrevPage}
          ></button>
          <div className="pagination-controls">
            <span className="pagination-info">
              {pagination.currentPage} из {pagination.totalPages}
            </span>
            <select
              className="pagination-select"
              value={pagination.currentPage}
              onChange={handleSelectChange}
              aria-label="Select page"
            >
              {Array.from(
                { length: pagination.totalPages },
                (_, i) => i + 1
              ).map((pageNum) => (
                <option key={pageNum} value={pageNum}>
                  {pageNum}
                </option>
              ))}
            </select>
          </div>
          <button
            className="pagination-button"
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={!pagination.hasNextPage}
          ></button>
        </div>
      )}
    </div>
  );
}

export default StreamerPage;
